// Service Worker Registration for TrackVAS PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  window.location.hostname === '[::1]' ||
  window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
};

// Check for updates every 60 seconds
const UPDATE_CHECK_INTERVAL = 60 * 1000;

// Service Worker Logger - provides structured logging for service worker events
const swLogger = {
  info: (message: string, data?: any) => {
    const timestamp = new Date().toISOString();
    console.info(`[SW ${timestamp}] ℹ️ ${message}`, data || '');
  },
  success: (message: string, data?: any) => {
    const timestamp = new Date().toISOString();
    console.log(`[SW ${timestamp}] ✅ ${message}`, data || '');
  },
  warn: (message: string, data?: any) => {
    const timestamp = new Date().toISOString();
    console.warn(`[SW ${timestamp}] ⚠️ ${message}`, data || '');
  },
  error: (message: string, error?: any) => {
    const timestamp = new Date().toISOString();
    console.error(`[SW ${timestamp}] ❌ ${message}`, error || '');
  },
};

export function register(config?: Config) {
  if ('serviceWorker' in navigator) {
    swLogger.info('Service Worker API is available');

    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      swLogger.warn('Public URL origin mismatch - Service Worker registration aborted');
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;
      swLogger.info(`Preparing to register Service Worker from: ${swUrl}`);

      if (isLocalhost) {
        swLogger.info('Localhost detected - running in development mode');
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          swLogger.success(
            'Service Worker active - App is being served cache-first by a service worker'
          );
        });
      } else {
        swLogger.info('Production mode - registering Service Worker');
        registerValidSW(swUrl, config);

        // Set up periodic update checks
        setInterval(() => {
          navigator.serviceWorker.getRegistration().then((registration) => {
            if (registration) {
              swLogger.info('Performing periodic Service Worker update check');
              registration.update();
            }
          });
        }, UPDATE_CHECK_INTERVAL);

        swLogger.success(`Periodic update checks configured (every ${UPDATE_CHECK_INTERVAL / 1000}s)`);
      }
    });
  } else {
    swLogger.warn('Service Worker API is not available in this browser');
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  swLogger.info('Starting Service Worker registration process');

  navigator.serviceWorker
    .register(swUrl)
    .then((registration) => {
      swLogger.success('Service Worker registered successfully', {
        scope: registration.scope,
        updateViaCache: registration.updateViaCache,
      });

      registration.onupdatefound = () => {
        swLogger.info('Service Worker update detected - new version found');
        const installingWorker = registration.installing;

        if (installingWorker == null) {
          swLogger.warn('Installing worker is null - update aborted');
          return;
        }

        installingWorker.onstatechange = () => {
          swLogger.info(`Service Worker state changed: ${installingWorker.state}`);

          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              swLogger.success(
                'New version available - Will be activated when all tabs are closed',
                { workerState: installingWorker.state }
              );

              if (config && config.onUpdate) {
                swLogger.info('Calling onUpdate callback');
                config.onUpdate(registration);
              }
            } else {
              swLogger.success(
                'Content is now cached for offline use - App ready for offline mode'
              );

              if (config && config.onSuccess) {
                swLogger.info('Calling onSuccess callback');
                config.onSuccess(registration);
              }
            }
          }
        };
      };
    })
    .catch((error) => {
      swLogger.error('Service Worker registration failed', error);
    });
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  swLogger.info('Validating Service Worker file');

  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      swLogger.info(`Service Worker file check - Status: ${response.status}, Content-Type: ${contentType}`);

      if (
        response.status === 404 ||
        (contentType != null && contentType.indexOf('javascript') === -1)
      ) {
        swLogger.warn(
          'Invalid Service Worker detected - File not found or wrong content type',
          { status: response.status, contentType }
        );

        navigator.serviceWorker.ready.then((registration) => {
          swLogger.info('Unregistering invalid Service Worker');
          registration.unregister().then(() => {
            swLogger.success('Invalid Service Worker unregistered - Reloading page');
            window.location.reload();
          });
        });
      } else {
        swLogger.success('Service Worker file is valid - Proceeding with registration');
        registerValidSW(swUrl, config);
      }
    })
    .catch((error) => {
      swLogger.warn('No internet connection - App is running in offline mode', error);
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    swLogger.info('Initiating Service Worker unregistration');

    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister().then((success) => {
          if (success) {
            swLogger.success('Service Worker unregistered successfully');
          } else {
            swLogger.warn('Service Worker unregistration returned false');
          }
        });
      })
      .catch((error) => {
        swLogger.error('Failed to unregister Service Worker', error);
      });
  } else {
    swLogger.warn('Service Worker API not available - Cannot unregister');
  }
}
